import { useEffect, useRef, useState } from 'react';
import Cropper from 'cropperjs';
import 'cropperjs/dist/cropper.css';

import { Col, Radio, RadioChangeEvent, Row } from 'antd';

import { GlobalConstants } from '@constants/global';
import { throttle } from '@utils/functions';

type TProps = {
    img: string;
    handleOnCrop: (img: string) => void;
    ratioSelection?: boolean;
    isAvatar?: boolean;
    isCRM?: boolean;
};

const ImgCropper = ({
    img,
    handleOnCrop,
    ratioSelection = true,
    isAvatar = false,
    isCRM,
}: TProps) => {
    const imageRef = useRef<HTMLImageElement>(null);
    const cropperRef = useRef<Cropper | null>(null);
    const [cropper, setCropper] = useState<Cropper | null>(null);
    const [ratio, setRatio] = useState(isAvatar ? 1 / 1 : 16 / 9);

    const setMinSize = (e: { detail: { width: number; height: number } }) => {
        const width = e.detail.width;
        const height = e.detail.height;
        const instance = cropperRef.current;
        if (!instance) {
            return;
        }

        if (
            width < GlobalConstants.UploadImg.minWidth ||
            height < GlobalConstants.UploadImg.minHeight ||
            width > GlobalConstants.UploadImg.maxWidth ||
            height > GlobalConstants.UploadImg.maxHeight
        ) {
            instance.setData({
                width: Math.max(
                    GlobalConstants.UploadImg.minWidth,
                    Math.min(GlobalConstants.UploadImg.maxWidth, width)
                ),
                height: Math.max(
                    GlobalConstants.UploadImg.minHeight,
                    Math.min(GlobalConstants.UploadImg.maxHeight, height)
                ),
            });
        }
    };

    const setCroppedImg = () => {
        const canvas = cropperRef.current?.getCroppedCanvas();
        if (canvas) {
            handleOnCrop(canvas.toDataURL());
        }
    };

    const thrSetCroppedImg = throttle(setCroppedImg, 70);

    const onCrop = (e: { detail: { width: number; height: number } }) => {
        setMinSize(e);
        thrSetCroppedImg();
    };

    useEffect(() => {
        const image = imageRef.current;
        if (!image) {
            return;
        }

        const instance = new Cropper(image, {
            viewMode: 2,
            aspectRatio: ratio,
            autoCropArea: 1,
            crop: onCrop,
        });
        cropperRef.current = instance;
        setCropper(instance);

        return () => {
            instance.destroy();
            cropperRef.current = null;
            setCropper(null);
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps -- remount on src change only; ratio via setAspectRatio
    }, [img]);

    useEffect(() => {
        cropper?.setAspectRatio(Number.isNaN(ratio) ? NaN : ratio);
    }, [cropper, ratio]);

    const handleChange = (e: RadioChangeEvent) => {
        const next = e.target.value === 'NaN' ? NaN : Number(e.target.value);
        setRatio(next);
    };

    return (
        <Row gutter={[0, 20]} justify="center">
            {!isAvatar && !isCRM && ratioSelection && (
                <Col span={24}>
                    <Radio.Group defaultValue={ratio} buttonStyle="solid" onChange={handleChange}>
                        <Radio.Button value={16 / 9}>16:9</Radio.Button>
                        <Radio.Button value={4 / 3}>4:3</Radio.Button>
                        <Radio.Button value={1 / 1}>1:1</Radio.Button>
                        <Radio.Button value={2 / 3}>2:3</Radio.Button>
                        <Radio.Button value={'NaN'}>свободная</Radio.Button>
                    </Radio.Group>
                </Col>
            )}

            <Col span={24}>
                <div style={{ maxHeight: `calc(100dvh - 250px)` }}>
                    <img
                        ref={imageRef}
                        src={img}
                        alt="crop"
                        style={{ opacity: 0, maxWidth: '100%', display: 'block' }}
                    />
                </div>
            </Col>
        </Row>
    );
};

export { ImgCropper };
